import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { graphQlClient, graphQlUpload } from '@config/graphqlClient';
import EmployeesGraphQL from '@graphql/employee.queries';
import { EMPLOYEES_QUERY_KEY } from '@hooks/api/employees/useEmployees';
import { store } from '@redux/store';
import { normalizeEmail } from '@utils/normalizeEmail';
import { format } from 'date-fns';
import { getErrorMessage, isNoSeatsAvailableError } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

export type EditUserArgs = {
  userId: string;
  employeeId?: string | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  dob?: string | Date | null;
  hireDate?: string | Date | null;
  rate?: number | null;
  payPeriod?: string | null;
  mode?: string | null;
  role?: string | null; // roleType in legacy API
  managerProjectIds?: string[] | null;
  superintendentProjectIds?: string[] | null;
  reviewerProjectIds?: string[] | null;
  file?: File | string | null; // Prefer File; string (base64) supported for backward compatibility
};

export const useEditEmployee = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['users', 'edit'],
    mutationFn: async (payload: EditUserArgs) => {
      const { userId: currentUserId } = store.getState().user || ({} as any);
      const localDate = new Date();

      const managerProjectsIds =
        (payload.managerProjectIds ?? []).length > 0 &&
        payload.role === 'PROJECT_MANAGER'
          ? payload.managerProjectIds
          : null;
      const superintendentProjectsIds =
        (payload.superintendentProjectIds ?? []).length > 0 &&
        payload.role === 'SUPERINTENDENT'
          ? payload.superintendentProjectIds
          : null;
      // Always send for reviewers (even empty) so clearing all projects works.
      const reviewerProjectsIds =
        payload.role === 'REVIEWER'
          ? (payload.reviewerProjectIds ?? [])
          : undefined;

      const employeeInput: any = {
        employeeId: payload.employeeId,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: normalizeEmail(payload.email),
        dob: payload.dob ? format(payload.dob as Date, 'yyyy-MM-dd') : null,
        hireDate: payload.hireDate
          ? format(payload.hireDate as Date, 'yyyy-MM-dd')
          : null,
        rate: payload.rate ?? undefined,
        selectedPayPeriod: payload.payPeriod ?? undefined,
        selectedMode: payload.mode ?? undefined,
        roleType: payload.role ?? undefined,
        userId: payload.userId,
        currentUserId,
        localDate: format(localDate, 'yyyy-MM-dd'),
        managerProjectsIds,
        superintendentProjectsIds,
        reviewerProjectsIds,
      };

      const query = EmployeesGraphQL.mutations.updateEmployee as any;

      if (payload.file && typeof payload.file !== 'string') {
        return await graphQlUpload(
          payload.file as File,
          'employee',
          query,
          employeeInput,
        );
      }

      if (payload.file && typeof payload.file === 'string') {
        employeeInput.file = payload.file;
      }

      return await graphQlClient.request(query, { employee: employeeInput });
    },
    onSuccess: () => {
      toast.success(tActionMessages.userUpdatedSuccessfully);
      queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
      // Invalidate active seats to update billing page
      queryClient.invalidateQueries({ queryKey: ['activeSeats'] });
    },
    onError: (error: any) => {
      // Sprint 20, story-093: the caller's onSeatsExhausted handler already opens the
      // out-of-seats modal for this code — the modal is the explanation, no toast needed.
      if (isNoSeatsAvailableError(error)) return;
      toast.error(getErrorMessage(error, tActionMessages.requestError));
    },
  });
};
